import React, { useCallback, useMemo, useState } from "react";

import {
    Box,
    Button,
    FormControl,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";

import { META_CUSTOM_AUDIENCE_SCOPES } from "Constant/CRM/facebook";
import { capitalizeFirstLetter } from "Constant/string";
import useUpdateEffect from "hooks/useUpdateEffect";
import { ClipLoader } from "react-spinners";

import { getMetaCustomAudiences } from "helpers/destinations/createDestination";

import FaceBookSdk from "components/Leads/SharedComponents/FaceBookSdk";
import SwalAlertComp from "components/SwalAlert";

const dataStrategyOptions = [
    {
        value: "add",
        label: "Add (Default)",
        description: "New users are added to the existing audiences.",
    },
    {
        value: "replace",
        label: "Replace",
        description:
            "The audience data is completely replaced. New data must be sent within a 90-minute session.",
    },
];
const MetaCustomAudience = ({
    isLoadingButton,
    fbExtras,
    appId,
    version,
    fbExternalBusinessID,
    setMetaCustomAudienceInfos,
    setIsSaveAvailable,
}) => {
    const [isLoading, setIsLoading] = useState(() => isLoadingButton);
    const [name, setName] = useState(null);
    const [showAdAccountSelect, setShowAdAccountSelect] = useState(true);
    const [addAccounts, setAddAccounts] = useState(null);
    const [partnerInstalID, setPartnerInstallID] = useState(null);
    const [selectedAddAccount, setSelectedAddAccount] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [selectedCustomAudience, setSelectedCustomAudience] = useState(null);
    const [retentionDays, setRetentionDays] = useState("180");
    const [businessManagerID, setBusinessManagerID] = useState(null);
    const [isSuccess, setIsSucess] = useState(false);
    const [isManualInput, setIsManualInput] = useState(false);
    const [strategy, setStrategy] = useState("add");

    const customAudiences = useMemo(
        () =>
            addAccounts?.find(
                (addAccount) => addAccount?.id === selectedAddAccount,
            )?.custom_audiences,
        [addAccounts, selectedAddAccount],
    );
    function handleFBError(error) {
        setIsLoading(false);

        let errorMessage = "";
        switch (error.code) {
            case 190: // Invalid OAuth Access Token
                errorMessage = "Session expired. Please log in again.";
                break;
            case 10: // Permission denied
                errorMessage = "Permission not granted.";
                break;
            default:
                errorMessage = false;
        }
        errorMessage &&
            SwalAlertComp({
                title: "Failed",
                message: errorMessage,
                icon: "error",
            });
    }

    const getCustomAudiences = useCallback(
        async ({ localBusinessManagerID, localAccessToken }) => {
            setIsLoading(true);

            const response = await getMetaCustomAudiences({
                access_token: localAccessToken || accessToken,
                external_business_id: fbExternalBusinessID,
                business_manager_id:
                    localBusinessManagerID || businessManagerID,
            });
            const data = response?.data;
            if (response.status) {
                setPartnerInstallID(data?.partner_install_id);
                setAddAccounts(data?.ad_accounts);
            }
            setIsLoading(false);
            setIsSucess(true);
        },
        [
            fbExternalBusinessID,
            setIsLoading,
            accessToken,
            businessManagerID,
            setPartnerInstallID,
            setAddAccounts,
        ],
    );

    const onLogin = async (onLoginAccessToken) => {
        setAccessToken(onLoginAccessToken);
        setAddAccounts(null);
        setPartnerInstallID(null);
        setSelectedAddAccount(null);
        setSelectedCustomAudience(null);
        setIsLoading(true);

        window.FB.api(
            "fbe_business/fbe_installs?fbe_external_business_id=" +
                fbExternalBusinessID,
            async function (fbResponse) {
                if (fbResponse.data && fbResponse.data.length > 0) {
                    const accountData = fbResponse.data[0];
                    const business_manager_id =
                        accountData?.business_manager_id;
                    setBusinessManagerID(business_manager_id);
                    getCustomAudiences({
                        localBusinessManagerID: business_manager_id,
                        localAccessToken: onLoginAccessToken,
                    });
                } else if (fbResponse.error) handleFBError(fbResponse);
            },
        );
    };

    useUpdateEffect(() => {
        setMetaCustomAudienceInfos({
            name,
            showAdAccountSelect,
            ad_account_id: selectedAddAccount,
            custom_audience_id: selectedCustomAudience,
            custom_audience_name: customAudiences?.find(
                ({ id }) => selectedCustomAudience === id,
            )?.name,
            partner_install_id: partnerInstalID,
            retention_days: retentionDays,
            strategy,
        });
        if (
            selectedAddAccount &&
            partnerInstalID &&
            ((showAdAccountSelect && selectedCustomAudience) ||
                (!showAdAccountSelect && name))
        )
            setIsSaveAvailable(true);
        else setIsSaveAvailable(false);
    }, [
        name,
        strategy,
        retentionDays,
        showAdAccountSelect,
        partnerInstalID,
        selectedCustomAudience,
        customAudiences?.length,
        selectedAddAccount,
    ]);

    return (
        <Box>
            <div className="position-relative overflow-hidden z-1 mb-3">
                {(isLoadingButton || isLoading) && (
                    <div className="circle-loader max-height-45">
                        <ClipLoader color={"#000"} size={17} />
                    </div>
                )}
                {!isSuccess && (
                    <FaceBookSdk
                        appId={appId}
                        updatedData={onLogin}
                        scope={META_CUSTOM_AUDIENCE_SCOPES}
                        text="Continue with META Custom Audience"
                        extras={fbExtras}
                        version={version}
                        setModalLoading={setIsLoading}
                    />
                )}
            </div>
            <FormControl fullWidth className="mt-3">
                <Select
                    label="Ad Account"
                    required
                    onChange={({ target: { value } }) =>
                        setSelectedAddAccount(value)
                    }
                    disabled={!addAccounts?.length}
                    className={!addAccounts?.length ? "disabled-inputs" : ""}
                >
                    {addAccounts?.map(({ id, name }) => (
                        <MenuItem value={id} key={id}>
                            {name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {showAdAccountSelect && (
                <FormControl fullWidth className="mt-3">
                    <Select
                        label="Custom Audience"
                        required
                        value={selectedCustomAudience}
                        onChange={({ target: { value } }) =>
                            setSelectedCustomAudience(value)
                        }
                        disabled={!selectedAddAccount}
                        className={!selectedAddAccount ? "disabled-inputs" : ""}
                    >
                        {customAudiences?.map(({ id, name }) => (
                            <MenuItem value={id} key={id}>
                                {name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {!showAdAccountSelect && (
                <FormControl fullWidth>
                    <TextField
                        name="name"
                        label={"Custom Audience Name"}
                        variant="outlined"
                        onChange={(e) => setName(e.target.value)}
                        className="mt-3"
                        value={name}
                        placeholder={"Name"}
                        fullWidth
                        focused
                    />
                </FormControl>
            )}
            {isManualInput && !showAdAccountSelect && (
                <TextField
                    label="Retention Days"
                    style={{
                        width: "100%",
                    }}
                    className={
                        !selectedAddAccount
                            ? "disabled-inputs text-field-ui-workaround mt-3"
                            : "text-field-ui-workaround mt-3"
                    }
                    type="number"
                    value={retentionDays}
                    onChange={({ target: { value } }) => {
                        if (
                            (value > 0 && value <= 180) ||
                            value === undefined
                        ) {
                            setRetentionDays(value);
                        }
                    }}
                />
            )}
            {!isManualInput && !showAdAccountSelect && (
                <FormControl fullWidth className="mt-3">
                    <Select
                        label="Retention Days"
                        required
                        value={retentionDays}
                        onChange={({ target: { value } }) => {
                            setRetentionDays(
                                value === "custom" ? "180" : value,
                            );
                            setIsManualInput(value === "custom");
                        }}
                        disabled={!selectedAddAccount}
                        className={!selectedAddAccount ? "disabled-inputs" : ""}
                    >
                        {[
                            "forever",
                            "1",
                            "7",
                            "15",
                            "30",
                            "60",
                            "90",
                            "180",
                            "custom",
                        ].map((value) => (
                            <MenuItem value={value} key={value}>
                                {capitalizeFirstLetter(value)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            <FormControl fullWidth className="mt-3">
                <Select
                    value={strategy}
                    onChange={(event) => {
                        setStrategy(event.target.value);
                    }}
                    fullWidth
                    label="Data Strategy"
                    size="small"
                    disabled={!selectedAddAccount}
                    aria-label="data-strategy"
                >
                    {dataStrategyOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            <div>
                                <Typography fontWeight={500}>
                                    {option.label}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {option.description}
                                </Typography>
                            </div>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <span className="d-flex justify-content-center mb-3 mt-3">
                {"OR"}
            </span>
            <FormControl
                fullWidth
                style={{
                    flexDirection: "row",
                    justifyContent: "center",
                }}
            >
                <Button
                    variant="outlined"
                    color="primary"
                    disabled={!selectedAddAccount}
                    fullWidth
                    onClick={() => {
                        setShowAdAccountSelect(!showAdAccountSelect);
                        setName(null);
                        setSelectedCustomAudience(null);
                    }}
                >
                    {showAdAccountSelect
                        ? "Create Custom Audience"
                        : "Select Existing Custom Audience"}
                </Button>
            </FormControl>
        </Box>
    );
};

export default MetaCustomAudience;
